// ─────────────────────────────────────────────────────────────
// LukaDuka - Password rules (ver2)
// src/shared/password/passwordRules.tsx
//
// SINGLE SOURCE OF TRUTH for the account-password wording and checks.
// The numbers themselves live in src/shared/constants/passwordRules.ts -
// this file turns them into what each screen shows. If the Supabase
// password policy ever changes, edit the constants file (and Supabase)
// only - not each screen.
//
// Changes from ver1:
//   Password minimum (3 Nov 2026): NIST SP 800-63B-4. 15 characters, no
//   required character mix, leaked-password check stays on (Supabase,
//   server-side). Replaces the 22 July 2026 policy (10 + lower/upper/
//   digit/symbol).
//   - ADD: passwordHint(), passwordTooShort(), passwordErrorMessage(),
//     PasswordHint (hint line + live "N more characters to go").
//   - passwordErrorMessage() uses Supabase's weak-password reasons list
//     ("pwned" = leaked) when present, falling back to text matching.
//     It NEVER returns raw Supabase text.
//   - REMOVED: the ver1 checklist (PASSWORD_RULES, checkPassword,
//     friendlyPasswordError, RuleRow, PasswordRulesChecklist). Every
//     screen now uses PasswordHint.
//   - PasswordHint uses the --ui-* tokens (lukaduka-shared.css), not a
//     hardcoded palette.
//
// Rules for every password field (do not break these):
//   - No app-side maximum below 64 characters.
//   - Never trim or strip spaces - phrases have spaces.
//   - Never block paste - password managers must work.
//   - Sign-in does NOT use any of this - older passwords must still work.
// ─────────────────────────────────────────────────────────────

import { isAuthWeakPasswordError } from '@supabase/supabase-js'
import { PASSWORD_MIN_LENGTH, PASSWORD_REQUIRES_MIX } from '@/shared/constants/passwordRules'

// Password minimum (3 Nov 2026): count characters as a person sees them
// (code points), so "ā" or an emoji is one character, not two. Supabase
// counts the same or more, so the app is never looser than the server.
function charCount(password: string): number {
  return Array.from(password).length
}

// Only used while PASSWORD_REQUIRES_MIX is on.
function meetsMix(password: string): boolean {
  return /[a-z]/.test(password) && /[A-Z]/.test(password)
    && /[0-9]/.test(password) && /[^A-Za-z0-9]/.test(password)
}

// Password minimum (3 Nov 2026): the hint line shown under the password box
// on sign-up, set new password and change password. The mix branch is kept
// in case PASSWORD_REQUIRES_MIX is ever switched back on.
export function passwordHint(): string {
  if (PASSWORD_REQUIRES_MIX) {
    return `At least ${PASSWORD_MIN_LENGTH} characters, including an upper case letter, a lower case letter, a number and a symbol.`
  }
  return `At least ${PASSWORD_MIN_LENGTH} characters. A phrase only you would know works well, like four words that mean something to you.`
}

const weakMessage = () => `That password isn't strong enough. ${passwordHint()}`
const LEAKED_MESSAGE = "That password has turned up in a data breach somewhere else, so it isn't safe to use. Please choose a different one."
const TOO_LONG_MESSAGE = "That's a little too long for us to accept. Please use a slightly shorter phrase."

// Password minimum (3 Nov 2026): the app-side check a screen runs when the
// button is tapped. Returns the message to show, or null if it is fine to
// send to Supabase (which still has the final say, incl. the leaked check).
export function passwordTooShort(password: string): string | null {
  if (charCount(password) < PASSWORD_MIN_LENGTH) {
    return `Please use at least ${PASSWORD_MIN_LENGTH} characters. A short phrase is easier to remember than a jumble.`
  }
  if (PASSWORD_REQUIRES_MIX && !meetsMix(password)) return weakMessage()
  return null
}

// Password minimum (3 Nov 2026): turn a Supabase password rejection into
// plain language. Returns null when the error is NOT about the password, so
// the screen can use its own message (e.g. an expired reset link). Never
// returns raw Supabase text.
export function passwordErrorMessage(error: unknown): string | null {
  if (!error) return null
  const m = String((error as { message?: unknown }).message ?? '').toLowerCase()
  const tooLong = /too long|longer than|maximum|at most|exceed/.test(m)

  // Preferred: Supabase's own reasons list ("length", "characters", "pwned").
  if (isAuthWeakPasswordError(error)) {
    if (error.reasons.includes('pwned')) return LEAKED_MESSAGE
    if (tooLong) return TOO_LONG_MESSAGE
    return weakMessage()
  }

  // Fallback: text matching, for errors that arrive without reasons.
  if (/known to be weak|easy to guess|pwned|leaked|breach|compromis/.test(m)) return LEAKED_MESSAGE
  if (tooLong && /password/.test(m)) return TOO_LONG_MESSAGE
  if (/weak|should be at least|at least \d|characters|complexity|lowercase|uppercase|digit|symbol|does not meet/.test(m)) {
    return weakMessage()
  }
  return null
}

// Password minimum (3 Nov 2026): the hint line, plus a gentle live line once
// typing starts - "N more characters to go" (muted) or "✓ Long enough"
// (success).
// TODO(success token): lukaduka-shared.css has no success token yet -
// awaiting the maintainer's choice. Until then the success line uses the
// muted token too (no hardcoded colour); the tick + weight carry the meaning.
export function PasswordHint({ password }: { password: string }) {
  const remaining = PASSWORD_MIN_LENGTH - charCount(password)
  const enough = remaining <= 0
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: '4px 2px',
      fontSize: 12.5, lineHeight: 1.45 }}>
      <div style={{ color: 'var(--ui-text-muted)' }}>{passwordHint()}</div>
      {password.length > 0 && (
        <div style={{ color: 'var(--ui-text-muted)', fontWeight: enough ? 600 : 400 }}>
          {enough
            ? '✓ Long enough'
            : `${remaining} more ${remaining === 1 ? 'character' : 'characters'} to go`}
        </div>
      )}
    </div>
  )
}
